import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import {
  useGetDatasetData,
  useGetDatasetMetadata
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  LoaderIcon,
  PremiumIcon
} from "@/assets/icons";
import { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import Pagination from "@/components/datasetManagement/Pagination";
import Badge from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import MultipleSearchWithSelect from "@/components/ui/MultipleSearchWithSelect";
import { NoDataMessage } from "@/components/ui/NoDataMessage";
import SearchWithSelect, {
  SearchFilterParams
} from "@/components/ui/searchWithSelect";
import { useDataset } from "@/hooks/map/useDataset";
import { usePagination } from "@/hooks/usePagination";
import { normalizeFilterValue } from "@/lib/utils";
import { RootState } from "@/store";
import {
  setDatasetHistory,
  setSelectedDatasetExplorer
} from "@/store/slice/datasetSlice";
import { resetUIDataConfig } from "@/store/slice/uiSlice";
import { Source } from "@/types/common";
import { DatasetDataType } from "@/types/dataset";
import { PoiParameterState } from "@/types/poiParameter";
import { generateFilterableAttributes } from "@/utils/dataset";
import {
  formatDatasetRows,
  generateColumnFilters,
  getTableState,
  includesStringFilter
} from "@/utils/table";

import { generateColumns } from "../datasetTable/columns";
import { DATASETS_PREVIEW_NO_DATA_MESSAGE_MAPPING } from "./data";
import TableData from "./TableData";
import { UnlockPremiumModal } from "./UnlockPremiumModal";

interface PreviewProps {
  onBack: () => void;
  selectedDatasetId: string | null;
  dataSource: Source;
  isMultipleSearchTableDataset?: boolean;
  withSearch?: boolean;
  poiParameterState?: PoiParameterState;
}

export interface SearchFilterState {
  query: string[] | null;
  query_type: DatasetDataType[] | null;
  query_column: string[] | null;
}

const TEST_ID_PREFIX = "dataset-explorer-preview";

const STATIC_DUMMY_ROWS = Array.from({ length: 5 }, () => [
  123435, 432543, 543985, 9934835, 7723840
]);

const DATA_LIMIT = {
  locked: 5,
  unlocked: 10
};
export const Preview = (props: PreviewProps) => {
  const {
    onBack,
    selectedDatasetId,
    dataSource,
    isMultipleSearchTableDataset,
    withSearch = true,
    poiParameterState
  } = props;
  const { datasets, rawDatasets, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );
  const { data: userProfile } = useGetUserProfileAPI();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleClearDataset } = useDataset();

  const { pageSize, setPageSize, currentPage, setCurrentPage } =
    usePagination();

  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: datasetMetadata, isLoading: isLoadingDatasetMetadata } =
    useGetDatasetMetadata(selectedDatasetId ?? "", {
      enabled: !!selectedDatasetId,
      queryKey: ["dataset-metadata", selectedDatasetId]
    });

  const isLocked = !datasetMetadata?.is_subscribed;

  const { data: datasetData, isLoading: isLoadingDatasetData } =
    useGetDatasetData(
      {
        id: selectedDatasetId ?? "",
        limit: isLocked ? DATA_LIMIT.locked : DATA_LIMIT.unlocked
      },
      { enabled: !!selectedDatasetId && !isLoadingDatasetMetadata }
    );

  const isLoading = isLoadingDatasetMetadata || isLoadingDatasetData;

  const data = useMemo(() => {
    const rows = datasetData?.rows ?? [];
    const columns = datasetData?.columns ?? [];

    /**
     * Since the API returns a maximum of 5 rows,
     * we append 5 random rows after the fifth row
     * to provide a blur effect during dataset preview,
     * enhancing the user experience.
     */
    const combinedRows =
      isLocked && rows.length === DATA_LIMIT.locked
        ? [...rows, ...STATIC_DUMMY_ROWS]
        : rows;

    if (!columns || columns.length === 0) {
      return [];
    }

    return combinedRows.map((row) =>
      row.reduce<DatasetDetailRow>((acc, valueItem, index) => {
        if (columns[index] && valueItem !== null && valueItem !== undefined) {
          acc[
            normalizeFilterValue(columns[index]?.code || columns[index]?.name)
          ] = valueItem;
        }
        return acc;
      }, {})
    );
  }, [isLocked, datasetData?.rows, datasetData?.columns]);

  const initialFilterState: SearchFilterState = useMemo(() => {
    const [firstAttribute] = generateFilterableAttributes(
      datasetData?.columns ?? []
    );

    return {
      query: null,
      query_type: firstAttribute?.type ? [firstAttribute.type] : null,
      query_column: firstAttribute?.value ? [firstAttribute.value] : null
    };
  }, [datasetData?.columns]);

  const [filterState, setFilterState] = useState<SearchFilterState>(() => ({
    query: null,
    query_type: null,
    query_column: null
  }));

  const columnFilters = useMemo(() => {
    return generateColumnFilters(filterState);
  }, [filterState]);

  const formattedData = useMemo(() => {
    return formatDatasetRows(data, datasetMetadata?.schema);
  }, [data, datasetMetadata?.schema]);

  const table = useReactTable<DatasetDetailRow>({
    data: formattedData,
    columns: generateColumns({
      columnsConfig: datasetMetadata?.schema ?? [],
      timezone: userProfile?.timezone
    }),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize,
        pageIndex: currentPage - 1
      }
    },
    onSortingChange: (newSorting) => {
      if (!isLocked) {
        setCurrentPage(1);
        setSorting(newSorting);
      }
    },
    getSortedRowModel: getSortedRowModel(),
    filterFns: {
      includesString: includesStringFilter
    },
    state: getTableState(currentPage, pageSize, sorting, columnFilters)
  });

  const handleSingleFilterChange = (params: SearchFilterParams) => {
    const normalizedParams: SearchFilterState = {
      query: params.query ? [params.query] : null,
      query_type: params.query_type ? [params.query_type] : null,
      query_column: params.query_column ? [params.query_column] : null
    };

    setFilterState(normalizedParams);
    setCurrentPage(1);
  };

  const [isOpenDialogUnlockPremium, setIsOpenDialogUnlockPremium] =
    useState(false);

  const handleOnClickPreview = async () => {
    if (!selectedDatasetId || !datasetData || !datasetMetadata) return;

    dispatch(setDatasetHistory({ rawDatasets, datasets, visualizationOrder }));
    dispatch(resetUIDataConfig());
    dispatch(setSelectedDatasetExplorer(datasetMetadata));
    handleClearDataset();

    if (poiParameterState) {
      navigate(`/?preview=true&id=${datasetMetadata.id}&poi=true`, {
        state: {
          datasetData,
          datasetMetadataSchema: datasetMetadata.schema,
          poiParameterState
        }
      });
    } else {
      navigate(`/?preview=true&id=${datasetMetadata.id}&explorer=true`, {
        state: { datasetData, datasetMetadataSchema: datasetMetadata.schema }
      });
    }
  };
  const messageMapping =
    (datasetData?.rows?.length ?? 0) <= 0 ? "no-data" : "no-result";

  return (
    <div className="flex flex-col py-8 px-8 h-full gap-6 relative">
      <div>
        <div className="flex flex-row items-center pb-2">
          <Breadcrumb>
            <BreadcrumbList className="!gap-1 items-center">
              <BreadcrumbItem>
                <BreadcrumbLink data-testid="breadcrumb-back-link">
                  Dataset Explorer
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ArrowRightIcon fill="#CECECE" width={12} height={12} />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>Preview Dataset</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {datasetMetadata?.license === "premium" && (
            <div className="ml-auto">
              <Badge className="bg-solid-green border-solid-green rounded-[4px] flex items-center justify-center gap-1">
                <PremiumIcon
                  className="fill-white w-4 h-4"
                  aria-label="Premium dataset"
                  title="Premium dataset"
                />
                <span className="text-white text-xs font-medium">Premium</span>
              </Badge>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ArrowLeftIcon
            fill="#CECECE"
            width={32}
            height={32}
            className="cursor-pointer"
            onClick={() => {
              onBack();
              dispatch(setSelectedDatasetExplorer(null));
            }}
            title="arrow-left"
          />
          <span className="text-2xl text-txtHeading font-semibold">
            {datasetMetadata?.name}
          </span>
          <div className="ml-auto flex items-center gap-2 ">
            <div className="text-sm flex flex-col items-end">
              <span className="text-txtPlaceholder">
                {dataSource === "bvt" ? "Partner Data" : "My Organization"}
              </span>
              <span className="text-txtBody font-semibold">
                {datasetMetadata?.provider?.name}
              </span>
            </div>
            <div className="w-10 h-10 border rounded-lg flex items-center justify-center">
              {datasetMetadata?.provider?.logo_asset_url ? (
                <img
                  src={datasetMetadata?.provider?.logo_asset_url}
                  alt={`logo-${datasetMetadata?.source}`}
                  className="h-8 w-8"
                />
              ) : null}
            </div>
          </div>
        </div>
        <span className="text-txtBody text-xs my-2">
          {datasetMetadata?.short_description}
        </span>
      </div>
      {withSearch && !isMultipleSearchTableDataset && (
        <SearchWithSelect<SearchFilterParams>
          options={generateFilterableAttributes(datasetData?.columns ?? [])}
          disabled={isLocked || (datasetData?.rows?.length ?? 0) <= 0}
          onParamsChange={handleSingleFilterChange}
          searchParams={(() => {
            const state =
              filterState.query_column && filterState.query_column.length > 0
                ? filterState
                : initialFilterState;

            return {
              query: state.query?.[0] ?? null,
              query_type: state.query_type?.[0] ?? null,
              query_column: state.query_column?.[0] ?? null
            };
          })()}
        />
      )}
      {withSearch && isMultipleSearchTableDataset && (
        <MultipleSearchWithSelect
          options={generateFilterableAttributes(datasetData?.columns ?? [])}
          disabled={isLocked || (datasetData?.rows?.length ?? 0) <= 0}
          onParamsChange={(params) => {
            setFilterState(params);
            setCurrentPage(1);
          }}
          searchParams={filterState}
        />
      )}
      {isLoading ? (
        <div
          className="flex flex-col items-center justify-center h-full text-center text-sm text-dim-gray gap-2 my-auto"
          data-testid="loading-state"
        >
          <LoaderIcon className="animate-spin" width={60} height={60} />
          <p className="text-txtPlaceholder">Loading...</p>
        </div>
      ) : (
        <>
          <TableData
            table={table}
            isLocked={isLocked}
            emptyDataMessage={
              <NoDataMessage
                message={
                  DATASETS_PREVIEW_NO_DATA_MESSAGE_MAPPING[messageMapping]
                }
              />
            }
          />

          {!isLocked ? (
            <Pagination
              table={table}
              pageSize={pageSize}
              setPageSize={setPageSize}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              totalData={table.getFilteredRowModel().rows.length}
            />
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-xs">
                *Sample data only. Purchase this dataset to gain full access.
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  data-testid={`${TEST_ID_PREFIX}-preview-onmap-button`}
                  variant="outline"
                  className="py-2.5 h-auto w-[200px] rounded-xl font-semibold text-sm"
                  onClick={handleOnClickPreview}
                  disabled={!datasetData}
                >
                  Preview on Map
                </Button>
                <Button
                  className="ml-auto w-[200px] h-[40px] py-2 border-teal-500 bg-teal-500 text-white text-sm font-bold disabled:bg-gainsboro disabled:border-none flex-none rounded-xl"
                  onClick={() => {
                    setIsOpenDialogUnlockPremium(true);
                  }}
                  data-testid={`${TEST_ID_PREFIX}-unlock-premium-button`}
                >
                  Unlock Premium
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {isOpenDialogUnlockPremium && (
        <UnlockPremiumModal
          title="Unlock Premium Dataset"
          description="To unlock this premium dataset, please contact your admin or reach out to our sales team to proceed with the purchase."
          contactUsDesc="If you have any questions about purchasing the dataset, feel free to get in touch:"
          emailSubject={`Request to Purchase Dataset - ${datasetMetadata?.name}`}
          emailBody={`Hi Bvarta team, I'm interested in accessing the dataset titled ${datasetMetadata?.name} from the source ${datasetMetadata?.source}. Please share details about licensing and pricing. Thanks!`}
          onClose={() => setIsOpenDialogUnlockPremium(false)}
        />
      )}
    </div>
  );
};
